import { useState } from "react";
import { Eye, MonitorSmartphone } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useToast } from "../ui/ToastContainer";
import { useAuth } from "../../contexts/AuthContext";
import { parseApiError } from "../../utils/errorHandler";
import {
  globalChangesService,
  type DeviceLimitBucket,
  type GlobalChangesPreviewResult,
} from "../../services/globalChangesService";
import {
  GlobalChangesCourseFilter,
  type GlobalChangesCourseFilterValue,
} from "./GlobalChangesCourseFilter";
import { GlobalChangesConfirmDialog } from "./GlobalChangesConfirmDialog";

const DEVICE_LIMIT_MIN = 0;
const DEVICE_LIMIT_MAX = 100;

const formatBucket = (bucket: DeviceLimitBucket): string =>
  `${bucket.count}× at ${bucket.value}`;

const buildFilterPayload = (filter: GlobalChangesCourseFilterValue) => {
  if (filter.allCourses) return { allCourses: true };
  return { allCourses: false, courseIds: filter.courseIds };
};

export function UpdateDeviceLimitCard() {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useToast();

  const canUpdate = hasPermission("globalChanges:deviceLimit");

  const [filter, setFilter] = useState<GlobalChangesCourseFilterValue>({
    allCourses: true,
    courseIds: [],
  });
  const [deviceLimit, setDeviceLimit] = useState("");

  const [preview, setPreview] =
    useState<GlobalChangesPreviewResult | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const filterEmpty = !filter.allCourses && filter.courseIds.length === 0;

  const parsedLimit = Number.parseInt(deviceLimit, 10);
  const limitValid =
    deviceLimit !== "" &&
    Number.isFinite(parsedLimit) &&
    parsedLimit >= DEVICE_LIMIT_MIN &&
    parsedLimit <= DEVICE_LIMIT_MAX;

  const filterPayload = buildFilterPayload(filter);

  const handlePreview = async () => {
    if (filterEmpty || !limitValid) return;
    setIsPreviewing(true);
    setPreview(null);
    try {
      const response = await globalChangesService.preview({
        ...filterPayload,
        operation: "deviceLimit",
      });
      setPreview(response);
    } catch (err) {
      const parsed = parseApiError(err);
      showError(parsed.message, "Preview Failed");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleChangeFilter = (next: GlobalChangesCourseFilterValue) => {
    setFilter(next);
    setPreview(null);
  };

  const handleChangeLimit = (raw: string) => {
    setDeviceLimit(raw);
    setPreview(null);
  };

  const handleOpenConfirm = () => {
    if (!preview || !limitValid) return;
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!preview || !limitValid) return;
    setIsApplying(true);
    try {
      const response = await globalChangesService.updateDeviceLimit({
        ...filterPayload,
        deviceLimit: parsedLimit,
      });
      showSuccess(
        `Updated device limit for ${response.modified} student${response.modified === 1 ? "" : "s"}.`,
        "Device Limit Applied",
      );
      setPreview(null);
      setDeviceLimit("");
      setIsConfirmOpen(false);
    } catch (err) {
      const parsed = parseApiError(err);
      showError(parsed.message, "Update Failed");
      setIsConfirmOpen(false);
    } finally {
      setIsApplying(false);
    }
  };

  const previewCount = preview?.studentsAffected ?? null;
  const bucketSummary =
    preview && preview.deviceLimitBuckets && preview.deviceLimitBuckets.length > 0
      ? preview.deviceLimitBuckets.map(formatBucket).join(", ")
      : null;

  return (
    <Card>
      <div className="space-y-5">
        <header className="space-y-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Update Student Device Limit
          </h3>
          <p className="text-sm text-gray-600">
            Override the device limit for every student with an enrollment in the
            selected courses. 0 means &quot;use site default&quot;.
          </p>
        </header>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Course filter
          </label>
          <GlobalChangesCourseFilter
            value={filter}
            onChange={handleChangeFilter}
            disabled={isApplying}
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="device-limit-input"
            className="block text-sm font-medium text-gray-700"
          >
            New device limit per student
          </label>
          <Input
            id="device-limit-input"
            type="number"
            min={DEVICE_LIMIT_MIN}
            max={DEVICE_LIMIT_MAX}
            value={deviceLimit}
            onChange={(e) => handleChangeLimit(e.target.value)}
            disabled={isApplying}
            placeholder="e.g. 3"
          />
          <p className="text-xs text-gray-500">
            0 = use site default · higher values override subscription and
            global defaults.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
          <Button
            variant="outline"
            size="md"
            onClick={handlePreview}
            disabled={
              filterEmpty || !limitValid || isPreviewing || isApplying
            }
            className="gap-2"
          >
            <Eye size={16} />
            {isPreviewing ? "Calculating..." : "Preview Impact"}
          </Button>
          <div className="text-sm text-gray-600">
            {previewCount === null ? (
              <p>Not yet previewed.</p>
            ) : (
              <div className="space-y-1">
                <p>
                  Will affect {previewCount.toLocaleString()} student
                  {previewCount === 1 ? "" : "s"}.
                </p>
                {bucketSummary && (
                  <p className="text-xs text-gray-500">
                    Currently: {bucketSummary}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {canUpdate ? (
          <div className="pt-2">
            <Button
              variant="primary"
              onClick={handleOpenConfirm}
              disabled={previewCount === null || !limitValid || isApplying}
              className="gap-2"
            >
              <MonitorSmartphone size={16} />
              Apply Device Limit
            </Button>
          </div>
        ) : (
          <p className="text-xs text-gray-500 pt-2">
            You lack the <code>globalChanges:deviceLimit</code> permission; this
            action is read-only.
          </p>
        )}

        <GlobalChangesConfirmDialog
          isOpen={isConfirmOpen}
          onClose={() => {
            if (!isApplying) setIsConfirmOpen(false);
          }}
          onConfirm={handleConfirm}
          title="Apply Device Limit"
          description={
            <span>
              This will overwrite the <strong>device limit</strong> on every
              matched student&apos;s user record. The change is written to the
              database immediately and cannot be undone from the UI.
            </span>
          }
          impactLine={
            previewCount !== null
              ? `This will affect ${previewCount.toLocaleString()} student${previewCount === 1 ? "" : "s"}.`
              : "No preview is available for the current selection."
          }
          isLoading={isApplying}
          confirmLabel="Apply Device Limit"
          largeNCount={previewCount ?? 0}
        />
      </div>
    </Card>
  );
}

export default UpdateDeviceLimitCard;